import {
  dataTypeArray,
  dataTypeKeysFromLabel,
  dateFormatKeysFromLabel,
  transportationKeys
} from '../../../../../../consts/conditions';
import { v4 } from 'uuid';
import { arithmeticKeysFromLabel, listOperators } from '../../../../../../consts/operators';
import {
  _filterType,
  _filterTypeItem
} from '../../../../../../hooks/transformations/useDefaultTransformation';
import { checkValueToZero } from '.';

export const normalizeTransformation = (data, id) => {
  function recursiveTraverseFilter(item) {
    if (item.type === 'group') {
      return {
        [item.conditionLabel]: [...item?.conditions]?.map(_cond => recursiveTraverseFilter(_cond))
      };
    }

    const isOperatorListType = listOperators.includes(item.operator);
    const value = isOperatorListType ? item.value.list : item.value[item?.inputType];
    const isValueInput = item?.inputType === 'input';
    const isValueColumn = item?.inputType === 'column';
    const isString = typeof value === 'string';
    const isDateTime = item?.inputType === 'datetime';

    return {
      column_name: item?.column,
      operator: item?.operator,
      comparison_value: {
        input:
          isOperatorListType || isValueInput
            ? isOperatorListType
              ? value
              : String(value) || (isString ? '' : null)
            : null,
        column_name: !isOperatorListType && isValueColumn ? value : null,
        datetime: isDateTime ? value : null,
        ai_input: {
          input: item?.value?.ai_input?.input || '',
          browser_use: item?.value?.ai_input?.browser_use,
          documents: item?.value?.ai_input?.documents || [],
          selected_columns: item?.value?.ai_input?.selected_columns || []
        }
      }
    };
  }

  function recursiveTraverseReplace(item) {
    if (item.type === 'group') {
      const isValueInput = item?.thenInputType === 'input';
      const isValueColumn = item?.thenInputType === 'column';
      const value = item.then[item?.thenInputType];
      const isString = typeof value === 'string';
      const isThenDateTime = item?.thenInputType === 'datetime';
      const isThenAi = item?.thenInputType === 'ai_input';

      if (!item.conditions.map(_cond => recursiveTraverseReplace(_cond)).length) return null;

      return {
        [item.conditionLabel]: [...item.conditions.map(_cond => recursiveTraverseReplace(_cond))],
        result_value: {
          input: isValueInput
            ? checkValueToZero(value) || value
              ? value
              : isString
                ? ''
                : null
            : null,

          column_name: isValueColumn ? value || null : null,
          datetime: isThenDateTime ? value : null,
          ai_input: {
            input: isThenAi ? item?.then?.ai_input?.input || '' : '',
            browser_use: item?.then?.ai_input?.browser_use,
            documents: item?.then?.ai_input?.documents || [],
            selected_columns: (isThenAi && (item?.then?.ai_input?.selected_columns || [])) || []
          }
        }
      };
    }

    const isOperatorListType = listOperators.includes(item.operator);
    const value = isOperatorListType ? item.value.list : item.value[item?.inputType];
    const isValueInput = item?.inputType === 'input';
    const isValueColumn = item?.inputType === 'column';
    const isString = typeof value === 'string' || isValueInput;
    const isDateTime = item?.inputType === 'datetime';

    return {
      column_name: item?.column,
      operator: item?.operator,
      function: item?.function,
      comparison_value: {
        input:
          isOperatorListType || isValueInput
            ? isOperatorListType
              ? value
              : String(value) || (isString ? '' : null)
            : null,
        column_name: !isOperatorListType && isValueColumn ? value : null,
        datetime: isDateTime ? value : null,
        ai_input: {
          input: item?.value?.ai_input?.input || '',
          browser_use: item?.value?.ai_input?.browser_use,
          documents: item?.value?.ai_input?.documents || [],
          selected_columns: item?.value?.ai_input?.selected_columns || []
        }
      }
    };
  }

  function recursiveTraverseMath(item) {
    if (item.type === 'group') {
      return {
        type: 'group',
        operations: [...item?.conditions]?.map(_cond => recursiveTraverseMath(_cond))
      };
    }

    if (item.actionItem === 'operator') {
      return {
        name: arithmeticKeysFromLabel[item?.value?.column || item?.value?.input],
        type: 'operation'
      };
    }

    return {
      name: item?.value?.[item?.inputType],
      type: item?.inputType === 'column' ? 'column' : 'number'
    };
  }

  function recursiveTraverseConcatenate(item) {
    if (item.actionItem === 'operator') {
      return {
        name: item?.value?.column || item?.value?.input,
        type: 'separator'
      };
    }

    return {
      name: item?.value?.[item?.inputType],
      type: item?.inputType
    };
  }

  let _steps;

  if (data[0]?.label === transportationKeys.FILTER)
    _steps = [...data].map(step => ({
      id: id || data?.[0]?.systemId || v4(),
      action: step.label,
      expression: recursiveTraverseFilter(step),
      note: step.note
    }));
  else if (data[0]?.label === transportationKeys.SUM_IF)
    _steps = [...data].map(step => ({
      id: id || data?.[0]?.systemId || v4(),
      action: step.label,
      expression: recursiveTraverseFilter(step),
      note: step.note
    }));
  else if (data[0]?.label === transportationKeys.REPLACE) {
    const elseInputType = data?.[0]?.elseInputType;
    const elseInputValue = data?.[0]?.elseValue[elseInputType];
    const isInputElseInputType = elseInputType === 'input';
    const isColumnElseInputType = elseInputType === 'column';
    const isDatetimeElseInputType = elseInputType === 'datetime';
    const elseDefaultValue = !elseInputValue && data?.[0]?.replaceColumn;
    const isString = typeof elseInputType === 'string';
    const mutatedColumnElseValue =
      elseDefaultValue || !isInputElseInputType ? elseInputValue || null : null;

    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: transportationKeys.REPLACE,
        note: data?.[0].note,
        expression: {
          else: {
            input: isInputElseInputType ? elseInputValue || (isString ? '' : null) : null,
            column_name: isColumnElseInputType ? mutatedColumnElseValue : null,
            datetime: isDatetimeElseInputType ? elseInputValue : null
          },
          replace_column: data?.[0]?.replaceColumn,
          when: [...data.map(item => recursiveTraverseReplace(item))].filter(el => el)
        }
      }
    ];
  } else if (data[0]?.label === transportationKeys.FILL_FORWARD) {
    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: transportationKeys.FILL_FORWARD,
        columns: [data[0]?.column],
        expression: { fill_type: data?.[0]?.operator },
        note: data?.[0]?.note
      }
    ];
  } else if (data[0]?.label === transportationKeys.ABSOLUTE) {
    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: transportationKeys.ABSOLUTE,
        note: data?.[0]?.note,
        expression: {
          column: data[0]?.column
        }
      }
    ];
  } else if (data[0]?.label === transportationKeys.NEGATIVE) {
    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: transportationKeys.NEGATIVE,
        note: data?.[0]?.note,
        expression: {
          column: data[0]?.column
        }
      }
    ];
  } else if (data[0]?.label === transportationKeys.ADD_COLUMN) {
    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: transportationKeys.ADD_COLUMN,
        note: data?.[0]?.note,
        expression: {
          fill_value: data[0]?.value[data[0]?.inputType],
          result_column: data[0]?.column,
          type: data[0]?.replaceColumn
        }
      }
    ];
  } else if (data[0]?.label === transportationKeys.REMOVE_DUPLICATES) {
    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: transportationKeys.REMOVE_DUPLICATES,
        note: data?.[0]?.note,
        expression: {
          columns: data?.[0]?.columns,
          keep: data?.[0]?.keep
        }
      }
    ];
  } else if (data?.[0]?.label === transportationKeys.RENAME_COLUMNS) {
    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: transportationKeys.RENAME_COLUMNS,
        note: data?.[0]?.note,
        expression: {
          renamed_columns: data?.map(el => {
            return {
              from: el.column,
              to: el.value.input
            };
          })
        }
      }
    ];
  } else if (data?.[0]?.label === transportationKeys.CHANGE_DATA_TYPES) {
    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: transportationKeys.CHANGE_DATA_TYPES,
        note: data?.[0]?.note,
        expression: {
          columns: data?.map(el => {
            const isValueKey =
              el.value.column && dataTypeArray.some(type => type.value === el.value.column);

            return {
              column: el.column,
              type: isValueKey ? el.value.column : dataTypeKeysFromLabel[el.value.column],
              existing_formats: el.currentFormats
                ?.map(item => dateFormatKeysFromLabel[item])
                ?.filter(el => el),
              manual_formats: el.currentFormats?.filter(item => !dateFormatKeysFromLabel[item])
            };
          })
        }
      }
    ];
  } else if (data?.[0]?.label === transportationKeys.ARITHMETIC) {
    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: 'math',
        note: data?.[0]?.note,
        expression: {
          operations: [...data?.[0]?.conditions]?.map(item => recursiveTraverseMath(item)),
          result_column: data?.[0]?.then?.column
        }
      }
    ];
  } else if (data?.[0]?.label === transportationKeys.CONCATENATE) {
    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: transportationKeys.CONCATENATE,
        note: data?.[0]?.note,
        expression: {
          operations: [...data?.[0]?.conditions]?.map(item => recursiveTraverseConcatenate(item)),
          result_column: data?.[0]?.then?.column
        }
      }
    ];
  } else if (data?.[0]?.label === transportationKeys.SPLIT) {
    const isDatetime = data?.[0].elseInputType === 'datetime';

    _steps = [...data].map(step => ({
      id: id || data?.[0]?.systemId || v4(),
      action: step.label,
      note: step.note,
      expression: {
        filter: recursiveTraverseFilter(step),
        column: step?.replaceColumn,
        split_inputs: isDatetime ? [] : [...step?.columns],
        split_date: !isDatetime ? [] : [...step?.columns],
        split_number: step?.columns?.length
      }
    }));
  } else if (data?.[0]?.label === transportationKeys.DATE_SHIFTING) {
    _steps = [...data].map(step => ({
      id: id || data?.[0]?.systemId || v4(),
      action: step.label,
      note: step.note,
      expression: {
        filter: recursiveTraverseFilter(step),
        shifting_method: step.shiftingMethod,
        time_period: step.timePeriod,
        amount: Number(step.periodAmount),
        column_name: step.columnName
      }
    }));
  } else if (data?.[0]?.label === transportationKeys.DUPLICATE_ROWS) {
    _steps = [...data].map(step => ({
      id: id || data?.[0]?.systemId || v4(),
      action: step.label,
      note: step.note,
      expression: {
        filter: recursiveTraverseFilter(step),
        count: Number(step?.replaceColumn)
      }
    }));
  } else if (data?.[0]?.label === transportationKeys.SUBSTITUTE) {
    _steps = [...data].map(step => ({
      id: id || data?.[0]?.systemId || v4(),
      action: step.label,
      note: step.note,
      expression: {
        column: step.replaceColumn,
        old_text: step.elseValue.input,
        new_text: step.then.input,
        instance_num: step.updateType === 'input' ? step.columnsCount : null
      }
    }));
  } else if (data?.[0]?.label === transportationKeys.SPLIT_BY_VALUE) {
    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: transportationKeys.SPLIT_BY_VALUE,
        note: data?.[0]?.note,
        expression: {
          column: data?.[0]?.replaceColumn,
          separator: data?.[0]?.then?.input,
          split_number: Number(data?.[0]?.numberOfSplits),
          direction: data?.[0]?.direction,
          expand_to_columns: data?.[0]?.expandedNewColumns || false,
          new_columns: data?.[0]?.columns
        }
      }
    ];
  } else if (data?.[0]?.label === transportationKeys.JOIN) {
    _steps = [
      {
        id: id || data?.[0]?.systemId || v4(),
        action: transportationKeys.JOIN,
        note: data?.[0]?.note,
        expression: {
          join_type: data?.[0]?.inputType,
          left_table: {
            left_columns: data?.[0]?.columns?.filter(el => el),
            left_on: data?.[0]?.conditions?.map(condition => condition?.column),
            fuzzy_join: data?.[0]?.conditions?.map(condition => condition?.fuzzyMatching)
          },
          right_table: {
            // In-memory sentinel `0` means "second table is a lookup"; on
            // the wire that's `extraction_id: null`.
            extraction_id: data?.[0]?.elseValue?.input === 0 ? null : data?.[0]?.elseValue?.input,
            right_columns: data?.[0]?.columns2?.filter(el => el),
            right_on: data?.[0]?.conditions?.map(condition => condition?.operator),
            table: data?.[0]?.then?.input
          }
        }
      }
    ];
  }

  return {
    steps: _steps
  };
};
